import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Download, Loader2, Package, Plus, Upload, X, XCircle } from 'lucide-react';
import { adminApi } from '../services/api';
import EtatVide from '../components/EtatVide';
import {
  annoncerErreur, annoncerSucces, EtatErreur, Squelette, SqueletteCarte, SuiviTeleversement,
  useTeleversement, validerFichier,
} from '../etats';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
// Les formats que le serveur accepte ailleurs pour un dépôt : une ressource
// que les organisations ne sauraient pas ouvrir ne sert à rien.
const FORMATS_KIT = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.txt', '.csv', '.zip'];

const CATEGORIES = ['Gouvernance', 'Administration', 'Finances', 'Ressources humaines', 'Suivi-évaluation', 'Communication'];

function SoumissionCard({ s, onValidate }) {
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(null);     // la décision en cours d'envoi

  const handle = async (statut) => {
    setLoading(statut);
    try { await onValidate(s.id, statut, comment); }
    finally { setLoading(null); }
  };

  return (
    <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-bold text-sm text-[var(--text-bold)]">{s.kit_titre}</p>
          <p className="text-xs text-[var(--text-muted)]">
            Projet #{s.projet} · {new Date(s.soumis_le).toLocaleDateString('fr-FR')}
          </p>
        </div>
        <span className={`inline-flex items-center px-2.5 py-1 rounded-controle text-[12px] font-semibold ${
          s.statut === 'en_attente' ? 'text-[var(--attention)] bg-[var(--attention-fond)]' :
          s.statut === 'valide' ? 'text-[var(--accent-texte)] bg-[var(--vert-pale)]' :
          'text-[var(--alerte)] bg-[var(--alerte-fond)]'
        }`}>
          {s.statut.replace('_', ' ')}
        </span>
      </div>

      {s.fichier_url && (
        <a href={s.fichier_url} target="_blank" rel="noreferrer"
          className="flex items-center gap-2 text-xs text-[var(--accent-texte)] font-bold hover:underline mb-3">
          <Download className="w-3.5 h-3.5" />
          Voir le document soumis
        </a>
      )}

      {s.statut === 'en_attente' && (
        <div className="pt-3 border-t border-[var(--border)]">
          <textarea
            placeholder="Commentaire (optionnel)"
            value={comment}
            onChange={e => setComment(e.target.value)}
            rows={2}
            className="champ resize-none mb-3"
          />
          <div className="flex gap-2">
            <button onClick={() => handle('valide')} disabled={!!loading} aria-busy={loading === 'valide' || undefined}
              className="act flex-1">
              {loading === 'valide' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Valider
            </button>
            <button onClick={() => handle('rejete')} disabled={!!loading} aria-busy={loading === 'rejete' || undefined}
              className="act act-danger flex-1">
              {loading === 'rejete' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
              Rejeter
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function KitAdmin({ user }) {
  const [kit, setKit] = useState([]);
  const [soumissions, setSoumissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [charge, setCharge] = useState(false);       // au moins une réponse reçue
  const [error, setError] = useState(null);
  const dejaCharge = useRef(false);
  const [activeTab, setActiveTab] = useState('ressources');
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ categorie: CATEGORIES[0], titre: '', description: '', modifiable: false });
  const [uploadFile, setUploadFile] = useState(null);
  const [erreurFichier, setErreurFichier] = useState('');
  const envoi = useTeleversement();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, s] = await Promise.all([adminApi.getKit(), adminApi.getSoumissionsKit()]);
      setKit(Array.isArray(k) ? k : []);
      setSoumissions(Array.isArray(s) ? s : []);
      setError(null);
      dejaCharge.current = true;
      setCharge(true);
    } catch (e) {
      // Une actualisation qui échoue ne retire pas ce qui est déjà affiché.
      if (dejaCharge.current) annoncerErreur(e.message || 'Le kit n’a pas pu être actualisé.');
      else setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const choisirFichier = (fichier) => {
    setUploadFile(fichier || null);
    setErreurFichier(fichier ? validerFichier(fichier, { extensions: FORMATS_KIT }) || '' : '');
  };

  // Fermer pendant un envoi l'annule : pas de transfert orphelin.
  const fermerUpload = () => {
    if (envoi.enCours) envoi.annuler();
    envoi.reinitialiser();
    setShowUpload(false);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || envoi.enCours) return;
    const refus = validerFichier(uploadFile, { extensions: FORMATS_KIT });
    if (refus) { setErreurFichier(refus); return; }
    const fd = new FormData();
    Object.entries(uploadForm).forEach(([k, v]) => fd.append(k, v));
    fd.append('fichier', uploadFile);
    try {
      await envoi.lancer({ url: `${API_BASE}/accompagnement/kit/`, donnees: fd });
      annoncerSucces(`« ${uploadForm.titre} » est publié dans le kit.`);
      envoi.reinitialiser();
      setShowUpload(false);
      setUploadFile(null);
      setUploadForm({ categorie: CATEGORIES[0], titre: '', description: '', modifiable: false });
      load();
    } catch {
      /* l'échec s'affiche sous le formulaire (SuiviTeleversement) */
    }
  };

  const handleValidateSoumission = async (id, statut, commentaire) => {
    try {
      await adminApi.validerSoumissionKit(id, statut, commentaire);
      annoncerSucces(statut === 'valide' ? 'Soumission validée.' : 'Soumission rejetée.');
      load();
    } catch (e) {
      annoncerErreur(e.message || 'La décision n’a pas pu être enregistrée.');
    }
  };

  const pendingCount = soumissions.filter(s => s.statut === 'en_attente').length;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-pastille bg-[var(--accent)]/15 flex items-center justify-center">
            <Package className="w-6 h-6 text-[var(--accent-texte)]" />
          </div>
          <div>
            <h1 className="titre-page">Kit ASSO PRO</h1>
            {loading && !charge
              ? <Squelette className="h-3 w-24 mt-1" />
              : <p className="text-sm text-[var(--text-muted)]">{kit.length} ressource{kit.length > 1 ? 's' : ''}</p>}
          </div>
        </div>
        {activeTab === 'ressources' && (
          <button onClick={() => setShowUpload(true)}
            className="act">
            <Plus className="w-4 h-4" />
            Ajouter une ressource
          </button>
        )}
      </div>

      {/* Tabs */}
      {/* Choix exclusif : le controle segmente du modele. Une barre
          soulignee se lisait comme une navigation vers d'autres pages. */}
      <div className="segmente max-w-full overflow-x-auto mb-6" role="group" aria-label="Sections">
        {[
          { key: 'ressources', label: charge ? `Ressources (${kit.length})` : 'Ressources' },
          { key: 'soumissions', label: `Soumissions${pendingCount > 0 ? ` (${pendingCount} en attente)` : ''}` },
        ].map(t => (
          <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
                  aria-pressed={activeTab === t.key} className="whitespace-nowrap shrink-0">
            {t.label}
          </button>
        ))}
      </div>

      {loading && !charge ? (
        <div className={`grid gap-4 ${activeTab === 'ressources' ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2'}`}>
          {[0, 1, 2].map(i => <SqueletteCarte key={i} />)}
        </div>
      ) : error && !charge ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte"><EtatErreur erreur={error} onReessayer={load} /></div>
      ) : activeTab === 'ressources' ? (
        kit.length === 0 ? (
          <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
            <EtatVide
              icone={Package}
              titre="Aucune ressource dans le kit"
              aide="Déposez un document, un modèle ou un outil : les organisations y accèdent depuis leur espace."
              action={<button type="button" onClick={() => setShowUpload(true)} className="act act-sm"><Plus className="w-4 h-4" />Ajouter une ressource</button>}
            />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {kit.map(k => (
              <div key={k.id} className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-[12px] font-semibold px-2 py-1 rounded-controle bg-[var(--accent)]/10 text-[var(--accent-texte)]">
                    {k.categorie}
                  </span>
                  {k.modifiable && (
                    <span className="text-[12px] font-semibold px-2 py-1 rounded-controle bg-[var(--info-fond)] text-[var(--text-bold)]">
                      Modifiable
                    </span>
                  )}
                </div>
                <h3 className="font-semibold text-[var(--text-bold)] mb-1">{k.titre}</h3>
                {k.description && <p className="text-xs text-[var(--text-muted)] mb-3 line-clamp-2">{k.description}</p>}
                {k.fichier_url && (
                  <a href={k.fichier_url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 text-xs text-[var(--accent-texte)] font-bold hover:underline">
                    <Download className="w-3.5 h-3.5" />
                    Télécharger
                  </a>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        soumissions.length === 0 ? (
          <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
            <EtatVide
              icone={Upload}
              titre="Aucune soumission reçue"
              aide="Les documents déposés par les organisations apparaîtront ici, en attente de validation."
            />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {soumissions.map(s => (
              <SoumissionCard key={s.id} s={s} onValidate={handleValidateSoumission} />
            ))}
          </div>
        )
      )}

      {/* Modal upload */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="bg-[var(--bg-card)] rounded-carte w-full max-w-lg p-8 shadow-carte">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <h3 className="text-xl font-semibold text-[var(--text-bold)]">Ajouter une ressource kit</h3>
              <button onClick={fermerUpload} className="act act-icone act-tertiaire" aria-label="Fermer">
                <X className="w-5 h-5 text-[var(--text-muted)]" />
              </button>
            </div>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block surtitre text-[var(--text-muted)] mb-1.5">Catégorie</label>
                <select value={uploadForm.categorie} onChange={e => setUploadForm(p => ({ ...p, categorie: e.target.value }))}
                  className="champ">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block surtitre text-[var(--text-muted)] mb-1.5">Titre *</label>
                <input required value={uploadForm.titre} onChange={e => setUploadForm(p => ({ ...p, titre: e.target.value }))}
                  className="champ" />
              </div>
              <div>
                <label className="block surtitre text-[var(--text-muted)] mb-1.5">Description</label>
                <textarea value={uploadForm.description} onChange={e => setUploadForm(p => ({ ...p, description: e.target.value }))}
                  rows={2} className="champ resize-none" />
              </div>
              <div>
                <label className="block surtitre text-[var(--text-muted)] mb-1.5">Fichier *</label>
                <input required type="file" accept={FORMATS_KIT.join(',')} onChange={e => choisirFichier(e.target.files[0])}
                  aria-invalid={!!erreurFichier || undefined}
                  className="w-full text-sm text-[var(--text-muted)] file:mr-3 file:py-2 file:px-4 file:rounded-controle file:border-0 file:text-[11px] file:font-semibold file: file:tracking-widest file:bg-[var(--accent)] file:text-[#0A1128] hover:file:opacity-90 cursor-pointer" />
                {erreurFichier && <p role="alert" className="mt-1.5 text-[13px] text-[var(--alerte)]">{erreurFichier}</p>}
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={uploadForm.modifiable} onChange={e => setUploadForm(p => ({ ...p, modifiable: e.target.checked }))}
                  className="w-4 h-4 accent-[var(--accent)]" />
                <span className="text-sm font-semibold text-[var(--text-main)]">Document modifiable (à remplir par l'association)</span>
              </label>
              <SuiviTeleversement televersement={envoi} nom={uploadForm.titre || uploadFile?.name} />
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={fermerUpload}
                  className="act act-contour flex-1">
                  Annuler
                </button>
                <button type="submit" disabled={envoi.enCours || !!erreurFichier} aria-busy={envoi.enCours || undefined}
                  className="act flex-1">
                  {envoi.enCours && <Loader2 className="w-4 h-4 animate-spin" />}
                  {envoi.enCours ? 'Envoi…' : 'Publier'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
